import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  BarChart as BarChartIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Home as HomeIcon,
  MenuBook as MenuBookIcon,
  Restaurant as RestaurantIcon,
  Send as SendIcon,
  ThumbDown as ThumbDownIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import WeeklyMenuCalendar, { CalendarMeal } from './WeeklyMenuCalendar';
import RecipeDatabase, { Recipe } from './RecipeDatabase';
import SeasonalPlanning from './SeasonalPlanning';
import CostAnalysis from './CostAnalysis';

const API = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100').replace(/\/api\/v\d+\/?$/, '');

interface MealPlan {
  id: string;
  planName: string;
  planCode?: string;
  planType?: string;
  status: string;
  isPublished?: boolean;
  startDate?: string;
  endDate?: string;
  targetServings?: number;
  totalRecipes?: number;
  estimatedCost?: number | string;
  description?: string;
}

interface NutritionalSummary {
  nutritionalSummary?: {
    totalCalories?: number;
    totalProtein?: number;
    totalCarbs?: number;
    totalFat?: number;
    averageCaloriesPerMeal?: number;
  };
  meetsTargets?: boolean;
}

interface PlanForm {
  planName: string;
  startDate: string;
  endDate: string;
  targetServings: string;
  caloriesMin: string;
  caloriesMax: string;
  proteinGrams: string;
  description: string;
}

interface ScheduleForm {
  recipeId: string;
  mealType: string;
  dayOfWeek: string;
  scheduledDate: string;
  plannedServings: string;
  notes: string;
}

interface SchoolOption {
  id: string;
  schoolCode: string;
  schoolName: string;
  pupilCount?: number;
  kitchenName?: string;
  kitchenCode?: string;
}

interface SchoolMealForecast {
  id: string;
  schoolId: string;
  schoolCode: string;
  schoolName: string;
  kitchenName?: string;
  kitchenCode?: string;
  periodType: 'daily' | 'weekly' | 'monthly';
  periodStartDate: string;
  periodEndDate: string;
  mealType: string;
  forecastServings: number;
  pupilCount?: number;
  status: ForecastStatus;
  notes?: string;
  submittedBy?: string;
  submittedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  metadata?: {
    dailyServings?: Record<string, number>;
  };
}

type ForecastStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'confirmed' | 'cancelled';

interface ForecastForm {
  schoolId: string;
  periodType: 'daily' | 'weekly' | 'monthly';
  periodStartDate: string;
  periodEndDate: string;
  mealType: string;
  dailyServings: Record<string, string>;
  pupilCount: string;
  status: ForecastStatus;
  notes: string;
}

interface PlanningPreview {
  mealPlan: MealPlan;
  schoolNetwork?: {
    totalKitchens?: number;
    centralKitchens?: number;
    satelliteKitchens?: number;
    totalSchoolsServed?: number;
  };
  demandPlan?: {
    totalScheduledServings?: number;
    scheduledMeals?: number;
    schoolMealForecast?: {
      basis?: string;
      activeSchools?: number;
      schoolsCovered?: number;
      unassignedSchools?: number;
      totalPupils?: number;
      totalForecastServings?: number;
      dailyForecast?: Array<{
        requiredDate: string;
        forecastServings: number;
        meals: number;
        schoolCount: number;
      }>;
      weeklyForecast?: Array<{
        weekStartDate: string;
        forecastServings: number;
        meals: number;
        schoolCount: number;
      }>;
      monthlyForecast?: Array<{
        month: string;
        forecastServings: number;
        meals: number;
        schoolCount: number;
      }>;
      kitchenForecast?: Array<{
        kitchenProfileId?: string;
        kitchenCode?: string;
        kitchenName?: string;
        recipeCode: string;
        recipeName: string;
        mealType: string;
        requiredDate: string;
        forecastServings: number;
        meals: number;
        schoolCount: number;
      }>;
      schoolForecast?: Array<{
        schoolCode: string;
        schoolName: string;
        kitchenName?: string;
        forecastServings: number;
        meals: number;
      }>;
    };
    recipeDemand?: Array<{
      recipeId: string;
      recipeCode: string;
      recipeName: string;
      plannedServings: number;
      unit: string;
      occurrences?: number;
      firstRequiredDate?: string;
      lastRequiredDate?: string;
    }>;
  };
  supplyPlan?: {
    reorderRecommendations?: Array<{
      itemCode: string;
      itemDescription: string;
      orderQuantity: number;
      unit: string;
      orderByDate: string;
      requiredDate: string;
      leadTimeDays: number;
      reason?: string;
    }>;
  };
  integration?: {
    planningServiceUrl?: string;
    mpsRecordsReady?: number;
  };
}

const authHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const todayIso = () => new Date().toISOString().slice(0, 10);

const addDaysIso = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const schoolDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const isSchoolDay = (date: Date) => {
  const day = date.getDay();
  return day >= 1 && day <= 5;
};

const dateRangeIso = (start: string, end: string) => {
  const dates: string[] = [];
  const cursor = new Date(start);
  const last = new Date(end);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(last.getTime())) return dates;
  while (cursor <= last) {
    if (isSchoolDay(cursor)) {
      dates.push(cursor.toISOString().slice(0, 10));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const chunkByWeek = (dates: string[]) => {
  const weeks: string[][] = [];
  let current: string[] = [];
  dates.forEach((date) => {
    const day = new Date(date).getDay();
    if (current.length > 0 && day === 1) {
      weeks.push(current);
      current = [];
    }
    current.push(date);
  });
  if (current.length > 0) weeks.push(current);
  return weeks;
};

const sumDailyServings = (dailyServings: Record<string, string>) =>
  Object.values(dailyServings).reduce((sum, value) => {
    const parsed = Number(value);
    return sum + (Number.isFinite(parsed) ? parsed : 0);
  }, 0);

const forecastStatusColor = (status: ForecastStatus): 'default' | 'primary' | 'success' | 'warning' | 'error' =>
  status === 'approved' || status === 'confirmed'
    ? 'success'
    : status === 'submitted'
      ? 'primary'
      : status === 'rejected'
        ? 'error'
        : status === 'cancelled'
          ? 'default'
          : 'warning';

const forecastStatusLabel = (status: ForecastStatus) =>
  status === 'confirmed' ? 'approved' : status;

const initialPlanForm = (): PlanForm => ({
  planName: `School Meals Week of ${todayIso()}`,
  startDate: todayIso(),
  endDate: addDaysIso(6),
  targetServings: '500000',
  caloriesMin: '450',
  caloriesMax: '900',
  proteinGrams: '18',
  description: 'Weekly school meal plan for Kenyan kitchen operations.',
});

const initialScheduleForm: ScheduleForm = {
  recipeId: '',
  mealType: 'lunch',
  dayOfWeek: 'monday',
  scheduledDate: '',
  plannedServings: '500000',
  notes: '',
};

const initialForecastForm = (): ForecastForm => ({
  schoolId: '',
  periodType: 'daily',
  periodStartDate: todayIso(),
  periodEndDate: todayIso(),
  mealType: 'lunch',
  dailyServings: {},
  pupilCount: '',
  status: 'draft',
  notes: '',
});

const toPlanCode = (name: string) => {
  const suffix = new Date().toISOString().replace(/\D/g, '').slice(0, 12);
  const base = name.toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 28);
  return `${base || 'MEAL-PLAN'}-${suffix}`;
};

const parseNumber = (value: string) => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseGrams = (value?: string) => {
  if (!value) return 0;
  const parsed = Number(String(value).replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapApiRecipe = (recipe: any): Recipe => ({
  id: recipe.id,
  icon: recipe.metadata?.icon || '🍽️',
  name: recipe.recipeName || 'Unnamed recipe',
  category: recipe.category || 'Recipe',
  servings: Number(recipe.servingSize || recipe.yieldQuantity || 0),
  time: recipe.totalTimeMinutes != null ? `${recipe.totalTimeMinutes} min` : '--',
  nutrition: {
    calories: Number(recipe.caloriesPerServing || recipe.nutritionalInfo?.calories || 0),
    protein: `${recipe.nutritionalInfo?.protein || 0}g`,
    carbs: `${recipe.nutritionalInfo?.carbs || 0}g`,
  },
  ingredients: Array.isArray(recipe.ingredients)
    ? recipe.ingredients.map((ingredient: any) => (
      `${ingredient.ingredientName || ingredient.name} - ${ingredient.quantity ?? ''} ${ingredient.unitOfMeasure || ''}`.trim()
    ))
    : [],
  preparation: recipe.instructions || recipe.description || 'No preparation instructions recorded.',
  isSchedulable: true,
});

const MealPlanning: React.FC = () => {
  const navigate = useNavigate();

  const [mealPlans, setMealPlans] = useState<MealPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [planMeals, setPlanMeals] = useState<CalendarMeal[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [slotLoading, setSlotLoading] = useState(false);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [planningPreview, setPlanningPreview] = useState<PlanningPreview | null>(null);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [schoolForecasts, setSchoolForecasts] = useState<SchoolMealForecast[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<MealPlan | null>(null);
  const [planForm, setPlanForm] = useState<PlanForm>(initialPlanForm());
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editingMeal, setEditingMeal] = useState<CalendarMeal | null>(null);
  const [deletingMeal, setDeletingMeal] = useState<CalendarMeal | null>(null);
  const [scheduleForm, setScheduleForm] = useState<ScheduleForm>(initialScheduleForm);
  const [forecastOpen, setForecastOpen] = useState(false);
  const [editingForecast, setEditingForecast] = useState<SchoolMealForecast | null>(null);
  const [viewingForecast, setViewingForecast] = useState<SchoolMealForecast | null>(null);
  const [deletingForecast, setDeletingForecast] = useState<SchoolMealForecast | null>(null);
  const [forecastDeleteLoading, setForecastDeleteLoading] = useState(false);
  const [forecastActionLoading, setForecastActionLoading] = useState<string | null>(null);
  const [rejectingForecast, setRejectingForecast] = useState<SchoolMealForecast | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [forecastForm, setForecastForm] = useState<ForecastForm>(initialForecastForm());

  const [nutritionDialogOpen, setNutritionDialogOpen] = useState(false);
  const [nutritionPlan, setNutritionPlan] = useState<MealPlan | null>(null);
  const [nutritionData, setNutritionData] = useState<NutritionalSummary | null>(null);
  const [nutritionLoading, setNutritionLoading] = useState(false);
  const [nutritionError, setNutritionError] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const selectedPlan = useMemo(
    () => mealPlans.find((plan) => plan.id === selectedPlanId) || null,
    [mealPlans, selectedPlanId],
  );

  const fetchMealPlans = useCallback(async () => {
    setPlansLoading(true);
    setPlansError(null);
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/meal-planning/meal-plans?limit=100`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Failed to load meal plans: ${resp.status}`);
      const data = await resp.json();
      const list: MealPlan[] = Array.isArray(data) ? data : (data.data ?? []);
      setMealPlans(list);
    } catch (err: unknown) {
      setPlansError((err as Error).message || 'Failed to load meal plans');
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const fetchRecipes = useCallback(async () => {
    setRecipesLoading(true);
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/recipes/active`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Failed to load recipes: ${resp.status}`);
      const data = await resp.json();
      setRecipes((Array.isArray(data) ? data : (data.data ?? [])).map(mapApiRecipe));
    } catch {
      setRecipes([]);
    } finally {
      setRecipesLoading(false);
    }
  }, []);

  const fetchSchools = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/schools`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Failed to load schools: ${resp.status}`);
      const data = await resp.json();
      setSchools(Array.isArray(data) ? data : (data.data ?? []));
    } catch {
      setSchools([]);
    }
  }, []);

  const fetchSchoolForecasts = useCallback(async (plan?: MealPlan | null) => {
    setForecastLoading(true);
    try {
      const params = new URLSearchParams();
      if (plan?.startDate) params.set('from', new Date(plan.startDate).toISOString().slice(0, 10));
      if (plan?.endDate) params.set('to', new Date(plan.endDate).toISOString().slice(0, 10));
      const resp = await fetch(`${API}/api/v1/kitchen-ops/school-meal-forecasts${params.toString() ? `?${params}` : ''}`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Failed to load school forecasts: ${resp.status}`);
      const data = await resp.json();
      setSchoolForecasts(Array.isArray(data) ? data : (data.data ?? []));
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Failed to load school forecasts', severity: 'error' });
      setSchoolForecasts([]);
    } finally {
      setForecastLoading(false);
    }
  }, []);

  const fetchPlanMeals = useCallback(async (planId: string) => {
    if (!planId) {
      setPlanMeals([]);
      return;
    }
    setSlotLoading(true);
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/meal-planning/meal-plans/${planId}/recipes`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Failed to load scheduled meals: ${resp.status}`);
      const data = await resp.json();
      setPlanMeals((Array.isArray(data) ? data : []).map((meal: any) => ({
        id: meal.id,
        recipeId: meal.recipeId,
        recipeName: meal.recipeName || meal.recipe?.recipeName || 'Recipe',
        mealType: meal.mealType,
        dayOfWeek: meal.dayOfWeek,
        scheduledDate: meal.scheduledDate,
        plannedServings: Number(meal.plannedServings || 0),
        caloriesPerServing: meal.caloriesPerServing,
        totalCalories: meal.totalCalories,
        notes: meal.notes,
      })));
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Failed to load scheduled meals', severity: 'error' });
      setPlanMeals([]);
    } finally {
      setSlotLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMealPlans();
    fetchRecipes();
    fetchSchools();
  }, [fetchMealPlans, fetchRecipes, fetchSchools]);

  useEffect(() => {
    if (!selectedPlanId && mealPlans.length > 0) {
      setSelectedPlanId(mealPlans[0].id);
    }
  }, [mealPlans, selectedPlanId]);

  useEffect(() => {
    fetchPlanMeals(selectedPlanId);
    setPlanningPreview(null);
  }, [fetchPlanMeals, selectedPlanId]);

  useEffect(() => {
    fetchSchoolForecasts(selectedPlan);
  }, [fetchSchoolForecasts, selectedPlan]);

  const openCreateDialog = () => {
    setEditingPlan(null);
    setPlanForm(initialPlanForm());
    setCreateOpen(true);
  };

  const openEditPlanDialog = (plan: MealPlan) => {
    setEditingPlan(plan);
    setPlanForm({
      planName: plan.planName,
      startDate: plan.startDate ? new Date(plan.startDate).toISOString().slice(0, 10) : todayIso(),
      endDate: plan.endDate ? new Date(plan.endDate).toISOString().slice(0, 10) : addDaysIso(6),
      targetServings: String(plan.targetServings || 500000),
      caloriesMin: '',
      caloriesMax: '',
      proteinGrams: '',
      description: plan.description || '',
    });
    setCreateOpen(true);
  };

  const handleSavePlan = async () => {
    try {
      const body = {
        ...(editingPlan ? {} : { planCode: toPlanCode(planForm.planName) }),
        planName: planForm.planName,
        description: planForm.description,
        planType: 'weekly',
        ...(editingPlan ? {} : { status: 'draft' }),
        targetAudience: 'students',
        startDate: planForm.startDate,
        endDate: planForm.endDate,
        targetServings: parseNumber(planForm.targetServings),
        nutritionalTargets: {
          caloriesMin: parseNumber(planForm.caloriesMin),
          caloriesMax: parseNumber(planForm.caloriesMax),
          proteinGrams: parseNumber(planForm.proteinGrams),
        },
        dietaryRestrictions: [],
        allergenExclusions: [],
        cuisinePreferences: ['kenyan-school-meals'],
        ...(editingPlan ? { updatedBy: 'frontend' } : { createdBy: 'frontend' }),
      };

      const resp = await fetch(`${API}/api/v1/kitchen-ops/meal-planning/meal-plans${editingPlan ? `/${editingPlan.id}` : ''}`, {
        method: editingPlan ? 'PATCH' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || `Create failed: ${resp.status}`);
      }
      const created = await resp.json();
      setCreateOpen(false);
      setEditingPlan(null);
      setSnackbar({ open: true, message: editingPlan ? 'Meal plan updated' : 'Meal plan created', severity: 'success' });
      await fetchMealPlans();
      setSelectedPlanId(created.id || editingPlan?.id || selectedPlanId);
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Failed to save meal plan', severity: 'error' });
    }
  };

  const openScheduleDialog = (slot?: { mealType: string; dayOfWeek: string; scheduledDate?: string }, recipe?: Recipe) => {
    setEditingMeal(null);
    const defaultScheduledDate = slot?.scheduledDate || (selectedPlan?.planType === 'monthly' ? selectedPlan.startDate || '' : '');
    const defaultScheduledDay = defaultScheduledDate
      ? schoolDays[new Date(`${defaultScheduledDate}T00:00:00`).getDay() - 1] || slot?.dayOfWeek || 'monday'
      : slot?.dayOfWeek || 'monday';
    setScheduleForm({
      recipeId: recipe?.id || recipes[0]?.id || '',
      mealType: slot?.mealType || 'lunch',
      dayOfWeek: defaultScheduledDay,
      scheduledDate: defaultScheduledDate,
      plannedServings: String(selectedPlan?.targetServings || 500000),
      notes: '',
    });
    setScheduleOpen(true);
  };

  const openEditMealDialog = (meal: CalendarMeal) => {
    setEditingMeal(meal);
    setScheduleForm({
      recipeId: meal.recipeId || '',
      mealType: meal.mealType,
      dayOfWeek: meal.dayOfWeek || 'monday',
      scheduledDate: meal.scheduledDate ? new Date(meal.scheduledDate).toISOString().slice(0, 10) : '',
      plannedServings: String(meal.plannedServings || selectedPlan?.targetServings || 500000),
      notes: meal.notes || '',
    });
    setScheduleOpen(true);
  };

  const handleScheduleMeal = async () => {
    if (!selectedPlanId) {
      setSnackbar({ open: true, message: 'Create or select a meal plan first', severity: 'error' });
      return;
    }
    if (selectedPlan?.planType === 'monthly' && !scheduleForm.scheduledDate) {
      setSnackbar({ open: true, message: 'Monthly meal plans require a scheduled date', severity: 'error' });
      return;
    }
    try {
      const selectedRecipe = recipes.find((recipe) => recipe.id === scheduleForm.recipeId);
      const body = {
        ...(editingMeal ? {} : { mealPlanId: selectedPlanId, recipeId: scheduleForm.recipeId }),
        mealType: scheduleForm.mealType,
        dayOfWeek: scheduleForm.dayOfWeek,
        scheduledDate: scheduleForm.scheduledDate || undefined,
        plannedServings: parseNumber(scheduleForm.plannedServings) || 1,
        sequenceOrder: planMeals.length + 1,
        nutritionalInfo: selectedRecipe ? {
          protein: parseGrams(selectedRecipe.nutrition.protein),
          carbs: parseGrams(selectedRecipe.nutrition.carbs),
          fat: 0,
        } : undefined,
        notes: scheduleForm.notes,
      };

      const resp = await fetch(`${API}/api/v1/kitchen-ops/meal-planning/meal-plan-recipes${editingMeal ? `/${editingMeal.id}` : ''}`, {
        method: editingMeal ? 'PATCH' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || `Schedule failed: ${resp.status}`);
      }
      setScheduleOpen(false);
      setEditingMeal(null);
      setPlanningPreview(null);
      setSnackbar({ open: true, message: editingMeal ? 'Meal updated' : 'Meal scheduled', severity: 'success' });
      await fetchPlanMeals(selectedPlanId);
      await fetchMealPlans();
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Failed to save scheduled meal', severity: 'error' });
    }
  };

  const handleMoveMeal = async (meal: CalendarMeal, slot: { mealType: string; dayOfWeek: string; scheduledDate?: string }) => {
    try {
      const body = {
        mealType: slot.mealType,
        dayOfWeek: slot.dayOfWeek,
        ...(slot.scheduledDate ? { scheduledDate: slot.scheduledDate } : {}),
      };
      const resp = await fetch(`${API}/api/v1/kitchen-ops/meal-planning/meal-plan-recipes/${meal.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || `Move failed: ${resp.status}`);
      }
      setPlanningPreview(null);
      setSnackbar({ open: true, message: 'Meal moved', severity: 'success' });
      await fetchPlanMeals(selectedPlanId);
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Failed to move meal', severity: 'error' });
    }
  };

  const handleDeleteMeal = async () => {
    if (!deletingMeal) return;
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/meal-planning/meal-plan-recipes/${deletingMeal.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || `Delete failed: ${resp.status}`);
      }
      setDeletingMeal(null);
      setPlanningPreview(null);
      setSnackbar({ open: true, message: 'Meal removed from plan', severity: 'success' });
      await fetchPlanMeals(selectedPlanId);
      await fetchMealPlans();
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Failed to remove meal', severity: 'error' });
    }
  };

  const defaultForecastEndDate = (periodType: ForecastForm['periodType'], start: string) => {
    const date = new Date(start);
    if (Number.isNaN(date.getTime())) return start;
    if (periodType === 'weekly') {
      const day = date.getDay();
      const daysUntilFriday = day === 6 ? 6 : day === 0 ? 5 : 5 - day;
      date.setDate(date.getDate() + daysUntilFriday);
    }
    if (periodType === 'monthly') {
      date.setMonth(date.getMonth() + 1);
      date.setDate(0);
    }
    return date.toISOString().slice(0, 10);
  };

  const selectedForecastSchool = useMemo(
    () => schools.find((school) => school.id === forecastForm.schoolId),
    [schools, forecastForm.schoolId],
  );

  const forecastDates = useMemo(
    () => dateRangeIso(forecastForm.periodStartDate, forecastForm.periodEndDate),
    [forecastForm.periodStartDate, forecastForm.periodEndDate],
  );

  const forecastWeeks = useMemo(() => chunkByWeek(forecastDates), [forecastDates]);

  const defaultSchoolServings = (schoolId = forecastForm.schoolId) => {
    const school = schools.find((item) => item.id === schoolId);
    return String(school?.pupilCount || 0);
  };

  const buildDailyServings = (
    start: string,
    end: string,
    defaultValue: string,
    existing: Record<string, string> = {},
  ) => dateRangeIso(start, end).reduce((record, date) => {
    record[date] = existing[date] ?? defaultValue;
    return record;
  }, {} as Record<string, string>);

  const openForecastDialog = (forecast?: SchoolMealForecast) => {
    setEditingForecast(forecast || null);
    if (forecast) {
      const start = new Date(forecast.periodStartDate).toISOString().slice(0, 10);
      const end = new Date(forecast.periodEndDate).toISOString().slice(0, 10);
      const defaultValue = String(forecast.pupilCount || forecast.forecastServings || 0);
      const existingDaily = Object.entries(forecast.metadata?.dailyServings || {}).reduce((record, [date, value]) => {
        record[date] = String(value);
        return record;
      }, {} as Record<string, string>);
      setForecastForm({
        schoolId: forecast.schoolId,
        periodType: forecast.periodType,
        periodStartDate: start,
        periodEndDate: end,
        mealType: forecast.mealType,
        dailyServings: buildDailyServings(start, end, defaultValue, existingDaily),
        pupilCount: String(forecast.pupilCount || ''),
        status: forecast.status || 'draft',
        notes: forecast.notes || '',
      });
    } else {
      const start = selectedPlan?.startDate ? new Date(selectedPlan.startDate).toISOString().slice(0, 10) : todayIso();
      const schoolId = schools[0]?.id || '';
      const defaultValue = defaultSchoolServings(schoolId);
      setForecastForm({
        ...initialForecastForm(),
        schoolId,
        periodStartDate: start,
        periodEndDate: start,
        dailyServings: buildDailyServings(start, start, defaultValue),
        pupilCount: defaultValue,
      });
    }
    setForecastOpen(true);
  };

  const updateForecastForm = (patch: Partial<ForecastForm>) => {
    setForecastForm((prev) => {
      const next = { ...prev, ...patch };
      let defaultValue = next.pupilCount || defaultSchoolServings(next.schoolId);
      if (patch.periodType || patch.periodStartDate) {
        next.periodEndDate = defaultForecastEndDate(next.periodType, next.periodStartDate);
      }
      if (patch.schoolId) {
        const school = schools.find((item) => item.id === patch.schoolId);
        defaultValue = String(school?.pupilCount || 0);
        next.pupilCount = defaultValue;
      }
      if (patch.pupilCount !== undefined) {
        defaultValue = patch.pupilCount;
      }
      if (patch.schoolId || patch.periodType || patch.periodStartDate || patch.periodEndDate || patch.pupilCount !== undefined) {
        next.dailyServings = buildDailyServings(
          next.periodStartDate,
          next.periodEndDate,
          defaultValue,
          patch.pupilCount !== undefined ? {} : prev.dailyServings,
        );
      }
      return next;
    });
  };

  const updateDailyServing = (date: string, value: string) => {
    setForecastForm((prev) => ({
      ...prev,
      dailyServings: {
        ...prev.dailyServings,
        [date]: value,
      },
    }));
  };

  const handleSaveForecast = async () => {
    const forecastServings = sumDailyServings(forecastForm.dailyServings);
    if (!forecastForm.schoolId || !forecastForm.periodStartDate || forecastServings <= 0) {
      setSnackbar({ open: true, message: 'School, period start, and at least one serving value are required', severity: 'error' });
      return;
    }
    try {
      const payload = {
        schoolId: forecastForm.schoolId,
        periodType: forecastForm.periodType,
        periodStartDate: forecastForm.periodStartDate,
        periodEndDate: forecastForm.periodEndDate,
        mealType: forecastForm.mealType,
        forecastServings,
        pupilCount: forecastForm.pupilCount ? Number(forecastForm.pupilCount) : undefined,
        status: forecastForm.status,
        notes: forecastForm.notes || undefined,
        metadata: {
          dailyServings: Object.fromEntries(
            Object.entries(forecastForm.dailyServings).map(([date, value]) => [date, Number(value) || 0]),
          ),
        },
        [editingForecast ? 'updatedBy' : 'createdBy']: 'frontend',
      };
      const resp = await fetch(`${API}/api/v1/kitchen-ops/school-meal-forecasts${editingForecast ? `/${editingForecast.id}` : ''}`, {
        method: editingForecast ? 'PATCH' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || `Forecast save failed: ${resp.status}`);
      setForecastOpen(false);
      setEditingForecast(null);
      setPlanningPreview(null);
      setSnackbar({ open: true, message: editingForecast ? 'School forecast updated' : 'School forecast created', severity: 'success' });
      await fetchSchoolForecasts(selectedPlan);
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Failed to save school forecast', severity: 'error' });
    }
  };

  const handleDeleteForecast = async () => {
    if (!deletingForecast) return;
    setForecastDeleteLoading(true);
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/school-meal-forecasts/${deletingForecast.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || `Forecast delete failed: ${resp.status}`);
      setDeletingForecast(null);
      setViewingForecast((current) => current?.id === deletingForecast.id ? null : current);
      setPlanningPreview(null);
      setSnackbar({ open: true, message: 'School forecast deleted', severity: 'success' });
      await fetchSchoolForecasts(selectedPlan);
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Failed to delete school forecast', severity: 'error' });
    } finally {
      setForecastDeleteLoading(false);
    }
  };

  const handleForecastApprovalAction = async (
    forecast: SchoolMealForecast,
    action: 'submit' | 'approve' | 'reject',
    reason?: string,
  ) => {
    setForecastActionLoading(`${forecast.id}-${action}`);
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/school-meal-forecasts/${forecast.id}/${action}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          userId: 'frontend',
          ...(reason ? { rejectionReason: reason, notes: reason } : {}),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || `Forecast ${action} failed: ${resp.status}`);
      setViewingForecast((current) => current?.id === forecast.id ? data : current);
      setRejectingForecast(null);
      setRejectionReason('');
      setPlanningPreview(null);
      setSnackbar({ open: true, message: `School forecast ${action === 'submit' ? 'submitted' : action === 'approve' ? 'approved' : 'rejected'}`, severity: 'success' });
      await fetchSchoolForecasts(selectedPlan);
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || `Failed to ${action} school forecast`, severity: 'error' });
    } finally {
      setForecastActionLoading(null);
    }
  };

  const renderForecastApprovalActions = (forecast: SchoolMealForecast) => {
    const isLoading = (action: 'submit' | 'approve' | 'reject') => forecastActionLoading === `${forecast.id}-${action}`;
    if (forecast.status === 'approved' || forecast.status === 'confirmed' || forecast.status === 'cancelled') return null;

    if (forecast.status === 'submitted') {
      return (
        <>
          <Tooltip title="Approve forecast">
            <Button
              size="small"
              color="success"
              variant="outlined"
              startIcon={<CheckCircleIcon fontSize="small" />}
              disabled={Boolean(forecastActionLoading)}
              onClick={() => handleForecastApprovalAction(forecast, 'approve')}
            >
              {isLoading('approve') ? 'Approving...' : 'Approve'}
            </Button>
          </Tooltip>
          <Tooltip title="Reject forecast">
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<ThumbDownIcon fontSize="small" />}
              disabled={Boolean(forecastActionLoading)}
              onClick={() => {
                setRejectingForecast(forecast);
                setRejectionReason('');
              }}
            >
              Reject
            </Button>
          </Tooltip>
        </>
      );
    }

    return (
      <Tooltip title={forecast.status === 'rejected' ? 'Resubmit forecast' : 'Submit forecast for approval'}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SendIcon fontSize="small" />}
          disabled={Boolean(forecastActionLoading)}
          onClick={() => handleForecastApprovalAction(forecast, 'submit')}
        >
          {isLoading('submit') ? 'Submitting...' : forecast.status === 'rejected' ? 'Resubmit' : 'Submit'}
        </Button>
      </Tooltip>
    );
  };

  const handlePublish = async (plan: MealPlan, publish: boolean) => {
    setActionLoading(plan.id);
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/meal-planning/meal-plans/${plan.id}/${publish ? 'publish' : 'unpublish'}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ userId: 'frontend' }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || `${publish ? 'Publish' : 'Unpublish'} failed: ${resp.status}`);
      }
      setSnackbar({ open: true, message: `${plan.planName} ${publish ? 'published' : 'unpublished'}`, severity: 'success' });
      await fetchMealPlans();
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Meal plan action failed', severity: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handlePreviewPlanning = async () => {
    if (!selectedPlanId) {
      setSnackbar({ open: true, message: 'Select a meal plan first', severity: 'error' });
      return;
    }
    setPlanningLoading(true);
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/planning/meal-plans/${selectedPlanId}/demand-supply`, {
        headers: authHeaders(),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || `Planning preview failed: ${resp.status}`);
      }
      setPlanningPreview(await resp.json());
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Failed to preview demand and supply', severity: 'error' });
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleSubmitDemandToPlanning = async () => {
    if (!selectedPlanId) {
      setSnackbar({ open: true, message: 'Select a meal plan first', severity: 'error' });
      return;
    }
    setSubmitLoading(true);
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/planning/meal-plans/${selectedPlanId}/submit-demand`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ activate: false }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || `Planning submission failed: ${resp.status}`);
      }
      const summary = data.summary || {};
      setSnackbar({
        open: true,
        message: `Submitted ${summary.submitted || 0} demand records; ${summary.skipped || 0} already existed.`,
        severity: summary.errors ? 'error' : 'success',
      });
      await handlePreviewPlanning();
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Failed to submit demand to planning', severity: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleViewNutrition = async (plan: MealPlan) => {
    setNutritionPlan(plan);
    setNutritionData(null);
    setNutritionError(null);
    setNutritionLoading(true);
    setNutritionDialogOpen(true);
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/meal-planning/meal-plans/${plan.id}/nutritional-summary`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Failed to load nutrition summary: ${resp.status}`);
      setNutritionData(await resp.json());
    } catch (err: unknown) {
      setNutritionError((err as Error).message || 'Failed to load nutritional summary');
    } finally {
      setNutritionLoading(false);
    }
  };

  const nutrition = nutritionData?.nutritionalSummary;

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link underline="hover" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} color="inherit" onClick={() => navigate('/')}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link underline="hover" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} color="inherit" onClick={() => navigate('/kitchen-ops')}>
            <RestaurantIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Kitchen Operations
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <MenuBookIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Meal Planning
          </Typography>
        </Breadcrumbs>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
              Meal Planning & Recipes
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Plan weekly menus, schedule recipes, publish approved plans, and validate nutrition.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            New Meal Plan
          </Button>
        </Stack>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Kenyan School Nutrition Guidelines</Typography>
          <Grid container spacing={2}>
            {[
              ['Traditional Staples', 'Ugali, rice, githeri, sweet potatoes, arrowroots, cassava'],
              ['Local Vegetables', 'Sukuma wiki, cabbage, spinach, managu, terere'],
              ['Protein Sources', 'Beans, lentils, green grams, fish, eggs, milk'],
              ['Fortification', 'Iron-fortified maize flour, Vitamin A oils, iodized salt, fortified milk'],
            ].map(([title, body]) => (
              <Grid item xs={12} sm={6} md={3} key={title}>
                <Box sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', minHeight: 112 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary">{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
            <Typography variant="h6">Meal Plans</Typography>
            <FormControl size="small" sx={{ minWidth: 280 }} disabled={mealPlans.length === 0}>
              <InputLabel id="selected-plan-label">Calendar Plan</InputLabel>
              <Select
                labelId="selected-plan-label"
                label="Calendar Plan"
                value={selectedPlanId}
                onChange={(event) => setSelectedPlanId(event.target.value)}
              >
                {mealPlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>{plan.planName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {plansError && <Alert severity="error" sx={{ mb: 2 }}>{plansError}</Alert>}

          {plansLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : mealPlans.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, border: '2px dashed #e0e0e0', borderRadius: 2, color: 'text.secondary' }}>
              <Typography variant="body1">No meal plans found</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>Create a weekly plan to start scheduling meals.</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Code</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Dates</strong></TableCell>
                    <TableCell><strong>Servings</strong></TableCell>
                    <TableCell><strong>Recipes</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mealPlans.map((plan) => (
                    <TableRow key={plan.id} hover selected={plan.id === selectedPlanId}>
                      <TableCell>{plan.planName}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{plan.planCode || '--'}</TableCell>
                      <TableCell>
                        <Chip
                          label={plan.isPublished ? 'Published' : plan.status}
                          color={plan.isPublished ? 'success' : plan.status === 'active' ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {plan.startDate ? new Date(plan.startDate).toLocaleDateString() : '--'} - {plan.endDate ? new Date(plan.endDate).toLocaleDateString() : '--'}
                      </TableCell>
                      <TableCell>{plan.targetServings?.toLocaleString() || '--'}</TableCell>
                      <TableCell>{plan.totalRecipes ?? 0}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Button size="small" variant="outlined" onClick={() => setSelectedPlanId(plan.id)}>Open</Button>
                          <Button size="small" variant="outlined" onClick={() => openEditPlanDialog(plan)}>Edit</Button>
                          <Button
                            size="small"
                            variant={plan.isPublished ? 'outlined' : 'contained'}
                            color={plan.isPublished ? 'warning' : 'success'}
                            disabled={actionLoading === plan.id}
                            onClick={() => handlePublish(plan, !plan.isPublished)}
                          >
                            {plan.isPublished ? 'Unpublish' : 'Publish'}
                          </Button>
                          <Tooltip title="View Nutritional Summary">
                            <IconButton size="small" onClick={() => handleViewNutrition(plan)} sx={{ color: 'primary.main' }}>
                              <BarChartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          {slotLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <WeeklyMenuCalendar
              meals={planMeals}
              selectedPlanName={selectedPlan?.planName}
              selectedPlanType={selectedPlan?.planType}
              selectedPlanStartDate={selectedPlan?.startDate}
              selectedPlanEndDate={selectedPlan?.endDate}
              onScheduleMeal={(slot) => openScheduleDialog(slot)}
              onEditMeal={openEditMealDialog}
              onDeleteMeal={setDeletingMeal}
              onMoveMeal={handleMoveMeal}
            />
          )}
        </Grid>
      </Grid>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6">School Meal Forecast Register</Typography>
              <Typography variant="body2" color="text.secondary">
                Capture daily, weekly, or monthly meal forecasts from schools. Only approved forecasts feed kitchen demand and supply planning.
              </Typography>
              {schoolForecasts.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {schoolForecasts.length.toLocaleString()} forecast{schoolForecasts.length === 1 ? '' : 's'} in the selected period
                </Typography>
              )}
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openForecastDialog()} disabled={schools.length === 0}>
              Add School Forecast
            </Button>
          </Stack>

          {schools.length === 0 ? (
            <Alert severity="warning">Add school master records before entering school meal forecasts.</Alert>
          ) : forecastLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : schoolForecasts.length === 0 ? (
            <Alert severity="info">No school forecasts found for the selected meal plan period.</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>School</strong></TableCell>
                    <TableCell><strong>Kitchen</strong></TableCell>
                    <TableCell><strong>Period</strong></TableCell>
                    <TableCell><strong>Meal</strong></TableCell>
                    <TableCell align="right"><strong>Servings</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schoolForecasts.map((forecast) => (
                    <TableRow key={forecast.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{forecast.schoolName}</Typography>
                        <Typography variant="caption" color="text.secondary">{forecast.schoolCode}</Typography>
                      </TableCell>
                      <TableCell>{forecast.kitchenName || 'Unassigned'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{forecast.periodType}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {forecast.periodStartDate ? new Date(forecast.periodStartDate).toLocaleDateString() : '--'} - {forecast.periodEndDate ? new Date(forecast.periodEndDate).toLocaleDateString() : '--'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{forecast.mealType}</TableCell>
                      <TableCell align="right">{Number(forecast.forecastServings || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={forecastStatusLabel(forecast.status)}
                          size="small"
                          color={forecastStatusColor(forecast.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center">
                          {renderForecastApprovalActions(forecast)}
                          <Tooltip title="View forecast">
                            <IconButton size="small" onClick={() => setViewingForecast(forecast)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit forecast">
                            <IconButton size="small" onClick={() => openForecastDialog(forecast)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete forecast">
                            <IconButton size="small" color="error" onClick={() => setDeletingForecast(forecast)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6">Demand & Supply Planning Integration</Typography>
              <Typography variant="body2" color="text.secondary">
                Push scheduled recipe demand into the existing planning service and review ingredient reorder timing.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="outlined" onClick={handlePreviewPlanning} disabled={!selectedPlanId || planningLoading}>
                {planningLoading ? 'Loading...' : 'Preview Demand/Supply'}
              </Button>
              <Button variant="contained" onClick={handleSubmitDemandToPlanning} disabled={!selectedPlanId || submitLoading || planningLoading}>
                {submitLoading ? 'Submitting...' : 'Submit Demand to Planning'}
              </Button>
            </Stack>
          </Stack>

          {!planningPreview ? (
            <Alert severity="info">Select a meal plan with scheduled recipes to preview demand and supply planning.</Alert>
          ) : (
            <Stack spacing={2}>
              {planningPreview.demandPlan?.schoolMealForecast?.unassignedSchools ? (
                <Alert severity="warning">
                  {planningPreview.demandPlan.schoolMealForecast.unassignedSchools} active schools are not assigned to a kitchen. They are included in the school forecast but will appear as unassigned kitchen demand.
                </Alert>
              ) : null}

              <Grid container spacing={2}>
                {[
                  ['Scheduled Servings', planningPreview.demandPlan?.totalScheduledServings || 0],
                  ['Scheduled Meals', planningPreview.demandPlan?.scheduledMeals || 0],
                  ['Active Schools', planningPreview.demandPlan?.schoolMealForecast?.activeSchools || 0],
                  ['Pupils Forecasted', planningPreview.demandPlan?.schoolMealForecast?.totalPupils || 0],
                  ['MPS Records Ready', planningPreview.integration?.mpsRecordsReady || 0],
                  ['Reorder Lines', planningPreview.supplyPlan?.reorderRecommendations?.length || 0],
                ].map(([label, value]) => (
                  <Grid item xs={6} md={2} key={label}>
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{Number(value).toLocaleString()}</Typography>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Monthly Forecast</Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell><strong>Month</strong></TableCell>
                          <TableCell align="right"><strong>School Meals</strong></TableCell>
                          <TableCell align="right"><strong>Servings</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(planningPreview.demandPlan?.schoolMealForecast?.monthlyForecast || []).map((line) => (
                          <TableRow key={line.month}>
                            <TableCell>{line.month}</TableCell>
                            <TableCell align="right">{Number(line.meals || 0).toLocaleString()}</TableCell>
                            <TableCell align="right">{Number(line.forecastServings || 0).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Weekly Forecast</Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell><strong>Week Start</strong></TableCell>
                          <TableCell align="right"><strong>Schools</strong></TableCell>
                          <TableCell align="right"><strong>Servings</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(planningPreview.demandPlan?.schoolMealForecast?.weeklyForecast || []).map((line) => (
                          <TableRow key={line.weekStartDate}>
                            <TableCell>{line.weekStartDate ? new Date(line.weekStartDate).toLocaleDateString() : '--'}</TableCell>
                            <TableCell align="right">{Number(line.schoolCount || 0).toLocaleString()}</TableCell>
                            <TableCell align="right">{Number(line.forecastServings || 0).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Daily Forecast</Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell align="right"><strong>School Meals</strong></TableCell>
                          <TableCell align="right"><strong>Servings</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(planningPreview.demandPlan?.schoolMealForecast?.dailyForecast || []).slice(0, 10).map((line) => (
                          <TableRow key={line.requiredDate}>
                            <TableCell>{line.requiredDate ? new Date(line.requiredDate).toLocaleDateString() : '--'}</TableCell>
                            <TableCell align="right">{Number(line.meals || 0).toLocaleString()}</TableCell>
                            <TableCell align="right">{Number(line.forecastServings || 0).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Kitchen Meal Forecast</Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell><strong>Kitchen</strong></TableCell>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell><strong>Meal</strong></TableCell>
                          <TableCell><strong>Recipe</strong></TableCell>
                          <TableCell align="right"><strong>Schools</strong></TableCell>
                          <TableCell align="right"><strong>Servings</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(planningPreview.demandPlan?.schoolMealForecast?.kitchenForecast || []).slice(0, 12).map((line, index) => (
                          <TableRow key={`${line.kitchenProfileId || 'unassigned'}-${line.requiredDate}-${line.recipeCode}-${line.mealType}-${index}`}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{line.kitchenName || 'Unassigned Kitchen'}</Typography>
                              <Typography variant="caption" color="text.secondary">{line.kitchenCode || '--'}</Typography>
                            </TableCell>
                            <TableCell>{line.requiredDate ? new Date(line.requiredDate).toLocaleDateString() : '--'}</TableCell>
                            <TableCell>{line.mealType}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{line.recipeName}</Typography>
                              <Typography variant="caption" color="text.secondary">{line.recipeCode}</Typography>
                            </TableCell>
                            <TableCell align="right">{Number(line.schoolCount || 0).toLocaleString()}</TableCell>
                            <TableCell align="right">{Number(line.forecastServings || 0).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Recipe Demand</Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell><strong>Recipe</strong></TableCell>
                          <TableCell align="right"><strong>Servings</strong></TableCell>
                          <TableCell align="right"><strong>Meals</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(planningPreview.demandPlan?.recipeDemand || []).map((line) => (
                          <TableRow key={line.recipeId}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{line.recipeName}</Typography>
                              <Typography variant="caption" color="text.secondary">{line.recipeCode}</Typography>
                            </TableCell>
                            <TableCell align="right">{Number(line.plannedServings || 0).toLocaleString()}</TableCell>
                            <TableCell align="right">{line.occurrences || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Supply Actions</Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell><strong>Item</strong></TableCell>
                          <TableCell align="right"><strong>Qty</strong></TableCell>
                          <TableCell><strong>Order By</strong></TableCell>
                          <TableCell align="right"><strong>Lead</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(planningPreview.supplyPlan?.reorderRecommendations || []).slice(0, 8).map((line) => (
                          <TableRow key={`${line.itemCode}-${line.requiredDate}`}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{line.itemDescription}</Typography>
                              <Typography variant="caption" color="text.secondary">{line.itemCode}</Typography>
                            </TableCell>
                            <TableCell align="right">{Number(line.orderQuantity || 0).toLocaleString()} {line.unit}</TableCell>
                            <TableCell>{line.orderByDate ? new Date(line.orderByDate).toLocaleDateString() : '--'}</TableCell>
                            <TableCell align="right">{line.leadTimeDays}d</TableCell>
                          </TableRow>
                        ))}
                        {(planningPreview.supplyPlan?.reorderRecommendations || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4}>
                              <Typography variant="body2" color="text.secondary">No reorder actions needed from current stock.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Stack>
          )}
        </CardContent>
      </Card>

      <SeasonalPlanning />
      <CostAnalysis />
      <RecipeDatabase
        recipes={recipes}
        loading={recipesLoading}
        canSchedule={Boolean(selectedPlanId && recipes.length > 0)}
        onScheduleRecipe={(recipe) => openScheduleDialog(undefined, recipe)}
      />

      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setEditingPlan(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingPlan ? 'Edit Meal Plan' : 'New Meal Plan'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Plan name" value={planForm.planName} onChange={(e) => setPlanForm({ ...planForm, planName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Start date" InputLabelProps={{ shrink: true }} value={planForm.startDate} onChange={(e) => setPlanForm({ ...planForm, startDate: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="End date" InputLabelProps={{ shrink: true }} value={planForm.endDate} onChange={(e) => setPlanForm({ ...planForm, endDate: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Target servings" value={planForm.targetServings} onChange={(e) => setPlanForm({ ...planForm, targetServings: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Min calories" value={planForm.caloriesMin} onChange={(e) => setPlanForm({ ...planForm, caloriesMin: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Max calories" value={planForm.caloriesMax} onChange={(e) => setPlanForm({ ...planForm, caloriesMax: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Protein target (g)" value={planForm.proteinGrams} onChange={(e) => setPlanForm({ ...planForm, proteinGrams: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label="Description" value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); setEditingPlan(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePlan} disabled={!planForm.planName || !planForm.startDate || !planForm.endDate}>
            {editingPlan ? 'Save Changes' : 'Create Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scheduleOpen} onClose={() => { setScheduleOpen(false); setEditingMeal(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMeal ? 'Edit Scheduled Meal' : 'Schedule Meal'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!selectedPlanId && <Alert severity="warning">Create or select a meal plan before scheduling meals.</Alert>}
            {recipes.length === 0 && <Alert severity="warning">No active backend recipes are available to schedule.</Alert>}
            <FormControl fullWidth disabled={Boolean(editingMeal)}>
              <InputLabel id="recipe-label">Recipe</InputLabel>
              <Select labelId="recipe-label" label="Recipe" value={scheduleForm.recipeId} onChange={(e) => setScheduleForm({ ...scheduleForm, recipeId: e.target.value })}>
                {editingMeal && scheduleForm.recipeId && !recipes.some((recipe) => recipe.id === scheduleForm.recipeId) && (
                  <MenuItem value={scheduleForm.recipeId}>{editingMeal.recipeName}</MenuItem>
                )}
                {recipes.map((recipe) => <MenuItem key={recipe.id} value={recipe.id}>{recipe.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="meal-type-label">Meal type</InputLabel>
                  <Select labelId="meal-type-label" label="Meal type" value={scheduleForm.mealType} onChange={(e) => setScheduleForm({ ...scheduleForm, mealType: e.target.value })}>
                    {['breakfast', 'lunch', 'snack', 'dinner'].map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="day-label">Day</InputLabel>
                  <Select labelId="day-label" label="Day" value={scheduleForm.dayOfWeek} onChange={(e) => setScheduleForm({ ...scheduleForm, dayOfWeek: e.target.value })}>
                    {schoolDays.map((day) => <MenuItem key={day} value={day}>{day}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              fullWidth
              type="date"
              label="Scheduled date"
              InputLabelProps={{ shrink: true }}
              value={scheduleForm.scheduledDate}
              onChange={(e) => {
                const scheduledDate = e.target.value;
                const date = new Date(`${scheduledDate}T00:00:00`);
                const day = schoolDays[date.getDay() - 1] || scheduleForm.dayOfWeek;
                setScheduleForm({ ...scheduleForm, scheduledDate, dayOfWeek: day });
              }}
            />
            <TextField fullWidth label="Planned servings" value={scheduleForm.plannedServings} onChange={(e) => setScheduleForm({ ...scheduleForm, plannedServings: e.target.value })} />
            <TextField fullWidth multiline minRows={2} label="Notes" value={scheduleForm.notes} onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setScheduleOpen(false); setEditingMeal(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleScheduleMeal}
            disabled={!selectedPlanId || (!editingMeal && (!scheduleForm.recipeId || recipes.length === 0))}
          >
            {editingMeal ? 'Save Changes' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={forecastOpen} onClose={() => { setForecastOpen(false); setEditingForecast(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingForecast ? 'Edit School Meal Forecast' : 'Add School Meal Forecast'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <FormControl fullWidth>
                <InputLabel id="forecast-school-label">School</InputLabel>
                <Select labelId="forecast-school-label" label="School" value={forecastForm.schoolId} onChange={(e) => updateForecastForm({ schoolId: e.target.value })}>
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.schoolName} ({school.schoolCode}){school.kitchenName ? ` - ${school.kitchenName}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {selectedForecastSchool && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Default servings are populated from {selectedForecastSchool.schoolName}: {Number(selectedForecastSchool.pupilCount || 0).toLocaleString()} pupils.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <Stack spacing={0.75}>
                <Typography variant="caption" color="text.secondary">Approval Status</Typography>
                <Box>
                  <Chip
                    label={forecastStatusLabel(forecastForm.status)}
                    size="small"
                    color={forecastStatusColor(forecastForm.status)}
                  />
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="forecast-period-type-label">Period Type</InputLabel>
                <Select labelId="forecast-period-type-label" label="Period Type" value={forecastForm.periodType} onChange={(e) => updateForecastForm({ periodType: e.target.value as ForecastForm['periodType'] })}>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="date" label="Period Start" InputLabelProps={{ shrink: true }} value={forecastForm.periodStartDate} onChange={(e) => updateForecastForm({ periodStartDate: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="date" label="Period End" InputLabelProps={{ shrink: true }} value={forecastForm.periodEndDate} onChange={(e) => updateForecastForm({ periodEndDate: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="forecast-meal-type-label">Meal Type</InputLabel>
                <Select labelId="forecast-meal-type-label" label="Meal Type" value={forecastForm.mealType} onChange={(e) => updateForecastForm({ mealType: e.target.value })}>
                  {['breakfast', 'brunch', 'lunch', 'dinner', 'snack'].map((mealType) => (
                    <MenuItem key={mealType} value={mealType}>{mealType}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Default Servings" value={forecastForm.pupilCount} onChange={(e) => updateForecastForm({ pupilCount: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {forecastForm.periodType === 'daily' ? 'Daily Forecast' : forecastForm.periodType === 'weekly' ? 'Week Days' : 'Monthly Days'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total: {sumDailyServings(forecastForm.dailyServings).toLocaleString()} servings
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  {forecastWeeks.map((week, weekIndex) => (
                    <Box key={`${week[0]}-${weekIndex}`}>
                      {forecastForm.periodType === 'monthly' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                          Week {weekIndex + 1}
                        </Typography>
                      )}
                      <Grid container spacing={1}>
                        {week.map((date) => (
                          <Grid item xs={12} sm={forecastForm.periodType === 'daily' ? 12 : 6} md={forecastForm.periodType === 'daily' ? 12 : 2.4} key={date}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label={new Date(date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                              value={forecastForm.dailyServings[date] ?? ''}
                              onChange={(e) => updateDailyServing(date, e.target.value)}
                              inputProps={{ min: 0 }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Notes" value={forecastForm.notes} onChange={(e) => updateForecastForm({ notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setForecastOpen(false); setEditingForecast(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveForecast} disabled={!forecastForm.schoolId || sumDailyServings(forecastForm.dailyServings) <= 0}>
            {editingForecast ? 'Save Changes' : 'Create Forecast'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deletingMeal)} onClose={() => setDeletingMeal(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Meal</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove {deletingMeal?.recipeName || 'this meal'} from the weekly plan?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingMeal(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteMeal}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(viewingForecast)} onClose={() => setViewingForecast(null)} maxWidth="md" fullWidth>
        <DialogTitle>School Meal Forecast</DialogTitle>
        <DialogContent>
          {viewingForecast && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">School</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{viewingForecast.schoolName}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewingForecast.schoolCode}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Kitchen</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{viewingForecast.kitchenName || 'Unassigned'}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewingForecast.kitchenCode || '--'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Period</Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{viewingForecast.periodType}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {viewingForecast.periodStartDate ? new Date(viewingForecast.periodStartDate).toLocaleDateString() : '--'} - {viewingForecast.periodEndDate ? new Date(viewingForecast.periodEndDate).toLocaleDateString() : '--'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Meal</Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 700 }}>{viewingForecast.mealType}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box>
                    <Chip
                      label={forecastStatusLabel(viewingForecast.status)}
                      size="small"
                      color={forecastStatusColor(viewingForecast.status)}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Total Servings</Typography>
                  <Typography variant="h6">{Number(viewingForecast.forecastServings || 0).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Default School Servings</Typography>
                  <Typography variant="h6">{Number(viewingForecast.pupilCount || 0).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Daily Entries</Typography>
                  <Typography variant="h6">{Object.keys(viewingForecast.metadata?.dailyServings || {}).length.toLocaleString()}</Typography>
                </Grid>
                {viewingForecast.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography variant="body2">{viewingForecast.notes}</Typography>
                  </Grid>
                )}
                {(viewingForecast.submittedAt || viewingForecast.approvedAt || viewingForecast.rejectedAt || viewingForecast.rejectionReason) && (
                  <Grid item xs={12}>
                    <Alert severity={viewingForecast.status === 'rejected' ? 'error' : viewingForecast.status === 'approved' || viewingForecast.status === 'confirmed' ? 'success' : 'info'}>
                      <Stack spacing={0.5}>
                        {viewingForecast.submittedAt && (
                          <Typography variant="body2">
                            Submitted by {viewingForecast.submittedBy || '--'} on {new Date(viewingForecast.submittedAt).toLocaleString()}
                          </Typography>
                        )}
                        {viewingForecast.approvedAt && (
                          <Typography variant="body2">
                            Approved by {viewingForecast.approvedBy || '--'} on {new Date(viewingForecast.approvedAt).toLocaleString()}
                          </Typography>
                        )}
                        {viewingForecast.rejectedAt && (
                          <Typography variant="body2">
                            Rejected by {viewingForecast.rejectedBy || '--'} on {new Date(viewingForecast.rejectedAt).toLocaleString()}
                          </Typography>
                        )}
                        {viewingForecast.rejectionReason && (
                          <Typography variant="body2">Reason: {viewingForecast.rejectionReason}</Typography>
                        )}
                      </Stack>
                    </Alert>
                  </Grid>
                )}
              </Grid>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Day</strong></TableCell>
                      <TableCell align="right"><strong>Servings</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(viewingForecast.metadata?.dailyServings || {})
                      .sort(([left], [right]) => left.localeCompare(right))
                      .map(([date, servings]) => (
                        <TableRow key={date}>
                          <TableCell>{new Date(`${date}T00:00:00`).toLocaleDateString()}</TableCell>
                          <TableCell>{new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long' })}</TableCell>
                          <TableCell align="right">{Number(servings || 0).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    {Object.keys(viewingForecast.metadata?.dailyServings || {}).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">No daily serving breakdown was stored for this forecast.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingForecast(null)}>Close</Button>
          {viewingForecast && (
            <>
              {renderForecastApprovalActions(viewingForecast)}
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  const forecast = viewingForecast;
                  setViewingForecast(null);
                  openForecastDialog(forecast);
                }}
              >
                Edit
              </Button>
              <Button
                color="error"
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  setDeletingForecast(viewingForecast);
                  setViewingForecast(null);
                }}
              >
                Delete
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(rejectingForecast)} onClose={() => setRejectingForecast(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject School Forecast</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Reject forecast for {rejectingForecast?.schoolName || 'this school'}?
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Rejection reason"
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectingForecast(null)} disabled={Boolean(forecastActionLoading)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectingForecast || !rejectionReason.trim() || Boolean(forecastActionLoading)}
            onClick={() => rejectingForecast && handleForecastApprovalAction(rejectingForecast, 'reject', rejectionReason)}
          >
            {forecastActionLoading?.endsWith('-reject') ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deletingForecast)} onClose={() => setDeletingForecast(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete School Forecast</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant="body2">
              Delete forecast for {deletingForecast?.schoolName || 'this school'}?
            </Typography>
            {deletingForecast && (
              <Alert severity="warning">
                This removes the {deletingForecast.periodType} {deletingForecast.mealType} forecast from demand planning for {Number(deletingForecast.forecastServings || 0).toLocaleString()} servings.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingForecast(null)} disabled={forecastDeleteLoading}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteForecast} disabled={forecastDeleteLoading}>
            {forecastDeleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={nutritionDialogOpen} onClose={() => setNutritionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nutritional Summary{nutritionPlan ? ` - ${nutritionPlan.planName}` : ''}</DialogTitle>
        <DialogContent>
          {nutritionLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
          {nutritionError && <Alert severity="error">{nutritionError}</Alert>}
          {!nutritionLoading && !nutritionError && nutritionData && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {[
                ['Calories', nutrition?.totalCalories ?? 0, 'kcal'],
                ['Protein', nutrition?.totalProtein ?? 0, 'g'],
                ['Carbohydrates', nutrition?.totalCarbs ?? 0, 'g'],
                ['Fat', nutrition?.totalFat ?? 0, 'g'],
              ].map(([label, value, unit]) => (
                <Grid item xs={6} key={label}>
                  <Box sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{Number(value).toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary">{unit}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{label}</Typography>
                  </Box>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Alert severity={nutritionData.meetsTargets ? 'success' : 'warning'}>
                  {nutritionData.meetsTargets ? 'This plan meets configured nutrition targets.' : 'This plan does not yet meet configured nutrition targets.'}
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNutritionDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MealPlanning;
